import React, { useMemo } from 'react';
import noop from 'lodash/noop';
import { Link, Stack, Typography } from '@mui/material';
import { WorkspaceSidebarProviderProps } from '@opengov/bnp-component-workflows-widgets';
import { createSelector } from '@opengov/compose-app/reselect';
import { getSession } from '@opengov/app-core/session/selectors';
import { Email } from '@opengov/react-capital-assets';
import { selectBnpHideOldEmailSettingsFlag } from '../flags';
import { getStandaloneOrigin } from './standaloneOrigin';

export interface INotificationPreferencesTabOptions {
  userId: number | null | undefined;
  hideOldEmailSettings: boolean;
}

export type NotificationPreferencesSidebarProps = Required<
  Pick<WorkspaceSidebarProviderProps, 'customTabsSections' | 'onCustomTabActiveChange'>
>;

export const NOTIFICATION_PREFERENCES_TAB_ID = 'notification-preferences';

const NOTIFICATION_PREFERENCES_TITLE = 'Notification Preferences';

export const getNotificationPreferencesUrl = (userId: number): string =>
  `${getStandaloneOrigin()}/controlpanel/users/${String(userId)}/notification_preferences/edit`;

const NotificationPreferencesPanel = ({ url }: { url: string }) => (
  <Stack spacing={1} data-test="notificationPreferencesPanel">
    <Typography>Your notification preferences open in a new tab.</Typography>
    <Link href={url} target="_blank" rel="noopener noreferrer" data-test="notificationPreferencesLink">
      Open notification preferences
    </Link>
  </Stack>
);

export const getNotificationPreferencesSidebarProps = ({
  userId,
  hideOldEmailSettings
}: INotificationPreferencesTabOptions): NotificationPreferencesSidebarProps => {
  if (!userId || hideOldEmailSettings) {
    return { customTabsSections: [], onCustomTabActiveChange: noop };
  }

  const url = getNotificationPreferencesUrl(userId);
  return {
    customTabsSections: [
      {
        title: '',
        tabs: [
          {
            id: NOTIFICATION_PREFERENCES_TAB_ID,
            tooltip: NOTIFICATION_PREFERENCES_TITLE,
            icon: <Email />,
            panelTitle: NOTIFICATION_PREFERENCES_TITLE,
            panelContent: <NotificationPreferencesPanel url={url} />
          }
        ]
      }
    ],
    // The sidebar has no link-only tabs before widgets 3.40, which needs bnp-core >= 3.17, so the
    // link is opened from the (synchronous) tab click and the panel keeps a fallback link.
    onCustomTabActiveChange: (tabId) => {
      if (tabId === NOTIFICATION_PREFERENCES_TAB_ID) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    }
  };
};

export const useNotificationPreferencesSidebarProps = (
  options: INotificationPreferencesTabOptions
): NotificationPreferencesSidebarProps => {
  const { userId, hideOldEmailSettings } = options;
  return useMemo(
    () => getNotificationPreferencesSidebarProps({ userId, hideOldEmailSettings }),
    [userId, hideOldEmailSettings]
  );
};

export const selectNotificationPreferencesTabOptions = createSelector(
  getSession,
  selectBnpHideOldEmailSettingsFlag,
  (session, hideOldEmailSettings): INotificationPreferencesTabOptions => ({
    userId: session.id,
    hideOldEmailSettings
  })
);
